// src/store/mapSlice.js
import { createSlice } from "@reduxjs/toolkit";

/**
 * Map slice manages UI state for the map view:
 * - Selected geography
 * - Highlighted tiers (for filtering)
 * - Map viewport
 * - UI visibility toggles (including competitors and opportunities)
 */

const initialState = {
  // Map viewport (will be overridden by config on load)
  mapCenter: [41.6032, -73.0877], // Connecticut center
  mapZoom: 9,

  // Selected geography
  selectedGeographyId: null,
  hoveredGeographyId: null,

  // Tier filtering - array of tier names that are highlighted
  // Empty array = show all tiers with their colors
  // Non-empty = highlight only these tiers, gray out others
  highlightedTiers: [],

  // UI visibility toggles
  legendVisible: true,
  detailsVisible: true,
  topGeographiesVisible: true,
  searchVisible: true,
  competitorsVisible: false,
  opportunitiesVisible: false,

  // Search state
  searchQuery: "",
  searchResults: [],
};

const mapSlice = createSlice({
  name: "map",
  initialState,
  reducers: {
    /**
     * Set map viewport (center and zoom)
     */
    setMapView: (state, action) => {
      const { center, zoom } = action.payload;
      if (center) state.mapCenter = center;
      if (zoom !== undefined) state.mapZoom = zoom;
    },

    /**
     * Set the selected geography
     */
    setSelectedGeography: (state, action) => {
      state.selectedGeographyId = action.payload;
    },

    /**
     * Clear the selected geography
     */
    clearSelectedGeography: (state) => {
      state.selectedGeographyId = null;
    },

    /**
     * Set the hovered geography
     */
    setHoveredGeography: (state, action) => {
      state.hoveredGeographyId = action.payload;
    },

    /**
     * Toggle a tier in the highlighted tiers array
     */
    toggleHighlightedTier: (state, action) => {
      const tier = action.payload.toLowerCase();
      const index = state.highlightedTiers.indexOf(tier);

      if (index > -1) {
        state.highlightedTiers.splice(index, 1);
      } else {
        state.highlightedTiers.push(tier);
      }
    },

    /**
     * Set highlighted tiers to a specific array
     */
    setHighlightedTiers: (state, action) => {
      state.highlightedTiers = action.payload.map((t) => t.toLowerCase());
    },

    /**
     * Clear all tier highlighting (show all tiers)
     */
    clearHighlightedTiers: (state) => {
      state.highlightedTiers = [];
    },

    /**
     * Toggle legend visibility
     */
    toggleLegend: (state) => {
      state.legendVisible = !state.legendVisible;
    },

    /**
     * Toggle details panel visibility
     */
    toggleDetails: (state) => {
      state.detailsVisible = !state.detailsVisible;
    },

    /**
     * Toggle top geographies panel visibility
     */
    toggleTopGeographies: (state) => {
      state.topGeographiesVisible = !state.topGeographiesVisible;
    },

    /**
     * Toggle search visibility
     */
    toggleSearch: (state) => {
      state.searchVisible = !state.searchVisible;
    },

    /**
     * Toggle competitors visibility
     */
    toggleCompetitors: (state) => {
      state.competitorsVisible = !state.competitorsVisible;
    },

    /**
     * Set competitors visibility explicitly
     */
    setCompetitorsVisible: (state, action) => {
      state.competitorsVisible = action.payload;
    },

    /**
     * Toggle opportunities visibility
     */
    toggleOpportunities: (state) => {
      state.opportunitiesVisible = !state.opportunitiesVisible;
    },

    /**
     * Set opportunities visibility explicitly
     */
    setOpportunitiesVisible: (state, action) => {
      state.opportunitiesVisible = action.payload;
    },

    /**
     * Set search query
     */
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },

    /**
     * Set search results
     */
    setSearchResults: (state, action) => {
      state.searchResults = action.payload;
    },

    /**
     * Clear search
     */
    clearSearch: (state) => {
      state.searchQuery = "";
      state.searchResults = [];
    },

    /**
     * Reset map state (on map switch or logout)
     */
    resetMapState: (state) => {
      state.selectedGeographyId = null;
      state.hoveredGeographyId = null;
      state.highlightedTiers = [];
      state.searchQuery = "";
      state.searchResults = [];
      state.competitorsVisible = false;
      state.opportunitiesVisible = false;
    },

    /**
     * Initialize map viewport from config
     */
    initializeFromConfig: (state, action) => {
      const { mapCenter, defaultZoom } = action.payload;
      if (mapCenter) state.mapCenter = mapCenter;
      if (defaultZoom !== undefined) state.mapZoom = defaultZoom;
    },
  },
});

export const {
  setMapView,
  setSelectedGeography,
  clearSelectedGeography,
  setHoveredGeography,
  toggleHighlightedTier,
  setHighlightedTiers,
  clearHighlightedTiers,
  toggleLegend,
  toggleDetails,
  toggleTopGeographies,
  toggleSearch,
  toggleCompetitors,
  setCompetitorsVisible,
  toggleOpportunities,
  setOpportunitiesVisible,
  setSearchQuery,
  setSearchResults,
  clearSearch,
  resetMapState,
  initializeFromConfig,
} = mapSlice.actions;

// ============================================
// SELECTORS
// ============================================

export const selectMapCenter = (state) => state.map.mapCenter;
export const selectMapZoom = (state) => state.map.mapZoom;
export const selectSelectedGeographyId = (state) =>
  state.map.selectedGeographyId;
export const selectHoveredGeographyId = (state) => state.map.hoveredGeographyId;
export const selectHighlightedTiers = (state) => state.map.highlightedTiers;
export const selectLegendVisible = (state) => state.map.legendVisible;
export const selectDetailsVisible = (state) => state.map.detailsVisible;
export const selectTopGeographiesVisible = (state) =>
  state.map.topGeographiesVisible;
export const selectSearchVisible = (state) => state.map.searchVisible;
export const selectSearchQuery = (state) => state.map.searchQuery;
export const selectSearchResults = (state) => state.map.searchResults;
export const selectCompetitorsVisible = (state) => state.map.competitorsVisible;
export const selectOpportunitiesVisible = (state) =>
  state.map.opportunitiesVisible;

/**
 * Check if any tier filtering is active
 */
export const selectHasActiveFilter = (state) =>
  state.map.highlightedTiers.length > 0;

/**
 * Check if a specific tier is highlighted
 */
export const selectIsTierHighlighted = (state, tier) => {
  if (state.map.highlightedTiers.length === 0) return true;
  return state.map.highlightedTiers.includes(tier.toLowerCase());
};

export default mapSlice.reducer;
